import React from 'react';

/**
 * PaymentMethodsStrip — row of branded payment-method badges in the footer.
 * Inline SVGs only (no external fetches, CSP-safe).
 *
 * Card-style badges at a proper 1.58:1 payment-card aspect ratio (72×44),
 * with brand marks scaled to fill the card face on a uniform baseline.
 */

const Badge = ({ label, children }) => (
  <div
    role="img"
    className="h-11 w-[72px] rounded-lg bg-white flex items-center justify-center shadow-sm ring-1 ring-black/5 transition-transform hover:-translate-y-0.5"
    aria-label={label}
    title={label}
  >
    {children}
  </div>
);

const VisaLogo = () => (
  <svg viewBox="0 0 52 16" className="h-[18px] w-auto" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <text
      x="26"
      y="13"
      textAnchor="middle"
      fontFamily="'Arial Black', Arial, sans-serif"
      fontWeight="900"
      fontSize="15"
      fontStyle="italic"
      fill="#1A1F71"
      letterSpacing="0.6"
    >
      VISA
    </text>
  </svg>
);

const MastercardLogo = () => (
  <svg viewBox="0 0 36 22" className="h-[22px] w-auto" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <circle cx="13" cy="11" r="9" fill="#EB001B" />
    <circle cx="23" cy="11" r="9" fill="#F79E1B" />
    <path d="M18 4 a9 9 0 0 1 0 14 a9 9 0 0 1 0 -14Z" fill="#FF5F00" />
  </svg>
);

const ApplePayLogo = () => (
  <svg viewBox="0 0 42 18" className="h-[18px] w-auto" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    {/* Apple glyph — 24x24 canonical path, scaled and centered vertically */}
    <g transform="translate(0 1.5) scale(0.625)">
      <path
        d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"
        fill="#000"
        transform="translate(-2 -3)"
      />
    </g>
    <text
      x="16"
      y="14"
      fontFamily="-apple-system, 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif"
      fontWeight="600"
      fontSize="13"
      fill="#000"
      letterSpacing="-0.4"
    >
      Pay
    </text>
  </svg>
);

const GooglePayLogo = () => (
  <svg viewBox="0 0 54 16" className="h-[16px] w-auto" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <text
      x="27"
      y="13"
      textAnchor="middle"
      fontFamily="'Product Sans', Roboto, Arial, sans-serif"
      fontWeight="600"
      fontSize="13"
      letterSpacing="-0.3"
    >
      <tspan fill="#4285F4">G</tspan>
      <tspan fill="#5F6368" dx="2">Pay</tspan>
    </text>
  </svg>
);

const SepaLogo = () => (
  <svg viewBox="0 0 48 14" className="h-[14px] w-auto" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <text
      x="24"
      y="11"
      textAnchor="middle"
      fontFamily="Arial, sans-serif"
      fontWeight="800"
      fontSize="12"
      fill="#10298E"
      letterSpacing="1"
    >
      SEPA
    </text>
  </svg>
);

const StripeLogo = () => (
  <svg viewBox="0 0 48 16" className="h-[16px] w-auto" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <text
      x="24"
      y="13"
      textAnchor="middle"
      fontFamily="'Inter', 'Helvetica Neue', Arial, sans-serif"
      fontWeight="800"
      fontSize="14"
      fill="#635BFF"
      letterSpacing="-0.4"
    >
      stripe
    </text>
  </svg>
);

export default function PaymentMethodsStrip() {
  return (
    <div
      className="flex flex-wrap items-center justify-center gap-3"
      role="list"
      aria-label="Accepted payment methods"
    >
      <div role="listitem"><Badge label="Visa"><VisaLogo /></Badge></div>
      <div role="listitem"><Badge label="Mastercard"><MastercardLogo /></Badge></div>
      <div role="listitem"><Badge label="Apple Pay"><ApplePayLogo /></Badge></div>
      <div role="listitem"><Badge label="Google Pay"><GooglePayLogo /></Badge></div>
      <div role="listitem"><Badge label="SEPA Direct Debit"><SepaLogo /></Badge></div>
      <div role="listitem"><Badge label="Stripe"><StripeLogo /></Badge></div>
    </div>
  );
}
